import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, AlertCircle, Pin, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { noticeApi, NoticeListItem } from '@/api/noticeApi';

const PAGE_SIZE = 10;

export default function NoticeListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicPage = location.pathname.startsWith('/notices');
  const noticeBasePath = isPublicPage ? '/notices' : '/dashboard/notices';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<NoticeListItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const fetchNotices = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await noticeApi.getList({ page, size: PAGE_SIZE });
      setNotices(res.data.content ?? []);
      setTotalPages(res.data.totalPages ?? 0);
      setTotalElements(res.data.totalElements ?? 0);
    } catch {
      setError('공지사항을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices(currentPage);
  }, [currentPage]);

  const orderedNotices = useMemo(
    () => [...notices].sort((a, b) => {
      if (a.isBanner !== b.isBanner) return Number(b.isBanner) - Number(a.isBanner);
      if (a.isPinned !== b.isPinned) return Number(b.isPinned) - Number(a.isPinned);
      return b.createdAt.localeCompare(a.createdAt);
    }),
    [notices]
  );

  const formatDate = (iso: string) => iso?.slice(0, 10) ?? '-';

  const pageContent = (
    <div className="space-y-6 p-4 md:p-8 max-w-5xl mx-auto">
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">공지사항</h1>
          <p className="text-gray-500 text-sm mt-1">서비스 관련 공지사항을 확인하세요</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-center">번호</TableHead>
                  <TableHead className="w-16 text-center">구분</TableHead>
                  <TableHead>제목</TableHead>
                  <TableHead className="w-28 text-right">작성일</TableHead>
                  <TableHead className="w-20 text-right">
                    <span className="flex items-center justify-end gap-1"><Eye className="w-3.5 h-3.5" />조회</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedNotices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-400 py-16">
                      등록된 공지사항이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  orderedNotices.map((notice, idx) => (
                    <TableRow
                      key={notice.id}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${notice.isPinned ? 'bg-blue-50/40' : ''}`}
                      onClick={() => navigate(`${noticeBasePath}/${notice.id}`)}
                    >
                      <TableCell className="text-center text-gray-500 text-sm">
                        {notice.isPinned ? (
                          <Pin className="w-3.5 h-3.5 text-primary mx-auto" />
                        ) : (
                          totalElements - (currentPage * PAGE_SIZE + idx)
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          {notice.isPinned && (
                            <Badge className="text-xs px-1.5 py-0 text-blue-600 border border-blue-300 bg-blue-50 rounded-full">고정</Badge>
                          )}
                          {notice.isBanner && (
                            <Badge className="text-xs px-1.5 py-0 text-orange-600 border border-orange-300 bg-orange-50 rounded-full">배너</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm ${notice.isPinned ? 'font-semibold' : ''}`}>
                          {notice.title}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-gray-500 text-sm">{formatDate(notice.createdAt)}</TableCell>
                      <TableCell className="text-right text-gray-500 text-sm">{(notice.viewCount ?? 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              variant={currentPage === i ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentPage(i)}
            >
              {i + 1}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage === totalPages - 1}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );

  if (isPublicPage) {
    return (
      <>
        <Helmet>
          <title>공지사항 - ALT Generation</title>
          <meta name="description" content="ALT Generation 서비스의 공지사항을 확인하세요. 업데이트 내용, 서비스 점검, 신규 기능 안내." />
          <link rel="canonical" href="https://alt.aigeneration-tech.com/notices" />
        </Helmet>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">{pageContent}</main>
          <Footer />
        </div>
      </>
    );
  }

  return pageContent;
}
